import { Component, ChangeDetectionStrategy } from '@angular/core';
import { IndicatorState } from '../consent-requests/consent-requests.component';
import Fund from '../../interfaces/fund';
import ConsentRequiredStatus from '../../interfaces/consent-required-status';
import ConsentRequestApproval from '../../interfaces/consent-request-approval';
import ConsentRequestInvestorApproval from '../../interfaces/consent-request-investor-approval';
import ConsentApprovalStatus from '../../interfaces/consent-approval-status';
import Organization from '../../interfaces/organization';
import { FormControl, FormGroup } from '@angular/forms';
import Project from '../../interfaces/project';
import { ConsentApprovalsService } from './consent-approvals.service';
import { ConsentRequestFilterInput } from '../consent-requests/graphql/consent-requests';
import {
  combineLatest,
  debounce,
  pairwise,
  startWith,
  Subscription,
  timer,
} from 'rxjs';
import { formatDate } from '@angular/common';
import { PageEvent } from '@angular/material/paginator';
import { ConsentRequest } from '../../interfaces/consent-request';
import ID from '../../types/id';
import CertificatedOutlet from '../../interfaces/certificated-outlet';
import { MatDialog } from '@angular/material/dialog';
import { IndividualChecklistDialogComponent } from '../consent-checklists/individual-checklist-dialog/individual-checklist-dialog.component';
import { PaginatorInfo } from '../../types/paginator';
import Sponsor from "../../interfaces/sponsor";
import Participant from "../../interfaces/participant";

export interface ApprovalsService {
  getApprovalStatus(
    consent_approval_status_id: ID | null
  ): ConsentApprovalStatus | null;
  getRequiredStatus(
    consent_required_status_id: ID | null
  ): ConsentRequiredStatus | null;
}
export type InvestorApprovalTableItem = {
  investor: Organization | null;
  approval_status: ConsentApprovalStatus | null;
  status_date: string | null;
  comments: string | null;
  owned_percent: number | null;
  investorApproval: ConsentRequestInvestorApproval;
};
export type ApprovalTableItem = {
  indicator: IndicatorState;
  fund: Fund | CertificatedOutlet | null;
  consent_required: ConsentRequiredStatus | null;
  sent_date: string | null;
  percent_required: number | null;
  fund_approval_date: string | null;
  investor_report_comment: string | null;
  current_percent: number | null;
  expanded: boolean;
  approval: ConsentRequestApproval;
  investorApprovals: InvestorApprovalTableItem[];
};
type RequestTableItem = {
  project: Project;
  requestType: string | null;
  checklist: IndicatorState;
  status: string | null;
  received: string;
  timing: string;
  is_urgent: boolean | null;
  urgency_note: string | null;
  details: string | null;
  developer: Organization | null;
  sponsor: Sponsor | null;
  approvals: ApprovalTableItem[];
  request: ConsentRequest;
};

export const _parseInvestorApprovals = (
  approval: ConsentRequestApproval,
  service: ApprovalsService
): InvestorApprovalTableItem[] => {
  let items: InvestorApprovalTableItem[] = [];
  if (approval.consentRequestInvestorApprovals) {
    for (let investorApproval of approval.consentRequestInvestorApprovals) {
      let status = service.getApprovalStatus(
        investorApproval.consent_approval_status_id
      );
      let item: InvestorApprovalTableItem = {
        investor: investorApproval.organization ?? null,
        approval_status: status ? status : null,
        owned_percent: investorApproval.owned_percentage,
        status_date: investorApproval.approval_date
          ? investorApproval.approval_date + 'T00:00:00'
          : null,
        comments: investorApproval.notes,
        investorApproval,
      };
      items.push(item);
    }
  }
  return items;
};
export const _parseApprovals = (
  approvals: ConsentRequestApproval[],
  service: ApprovalsService,
  readOnly: boolean = true
): ApprovalTableItem[] => {
  let approvalItems = [];
  for (let approval of approvals) {
    let item: ApprovalTableItem = {
      indicator: IndicatorState.INITIAL,
      fund: approval.fund ?? approval.certificatedOutlet ?? null,
      consent_required: service.getRequiredStatus(
        approval.consent_required_status_id
      ),
      sent_date: approval.investor_sent_date
        ? approval.investor_sent_date + 'T00:00:00'
        : null,
      percent_required: approval.percent_approval_required
        ? +(approval.percent_approval_required * 100).toFixed(2)
        : null,
      current_percent: approval.current_percent_approved,
      investor_report_comment: approval.investor_report_comment,
      fund_approval_date: approval.fund_approval_date
        ? approval.fund_approval_date + 'T00:00:00'
        : null,
      expanded: !readOnly,
      investorApprovals: _parseInvestorApprovals(approval, service),
      approval: approval,
    };
    item.investorApprovals.sort((a, b) => {
      if (a?.investor?.name !== undefined && b?.investor?.name !== undefined) {
        if (a.investor?.name > b.investor?.name) {
          return 1;
        }
        if (b.investor?.name > a.investor?.name) {
          return -1;
        }
      }
      return 0;
    });
    if (item.consent_required) {
      if (
        item.consent_required.required_status === 'Required' &&
        approval.percent_approval_required
      ) {
        if (
          approval.current_percent_approved &&
          approval.current_percent_approved >=
            approval.percent_approval_required
        ) {
          item.indicator = IndicatorState.SUCCESS;
        } else if (
          approval.current_percent_denied &&
          approval.current_percent_denied >=
            1 - approval.percent_approval_required
        ) {
          item.indicator = IndicatorState.ERROR;
        }
      } else if (item.consent_required.required_status !== 'Required') {
        item.indicator = IndicatorState.SUCCESS;
      }
    }
    approvalItems.push(item);
  }
  return approvalItems;
};
@Component({
    selector: 'app-consent-approvals',
    templateUrl: './consent-approvals.component.html',
    styleUrls: ['./consent-approvals.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ConsentApprovalsComponent {
  public filterControls = new FormGroup({
    project_id: new FormControl<Project | string | null>(null),
    received_date: new FormControl<string | Date | null>(null),
    timing_date: new FormControl<string | Date | null>(null),
    consent_status_id: new FormControl<string | null>(null),
    consent_request_type_id: new FormControl<string | null>(null),
  });
  public filters: ConsentRequestFilterInput = {};
  private formSubscription: Subscription;
  public loadingSub: Subscription;
  public currentPage: number = 1;
  public total = 1;
  public perPage = 1;
  public data: RequestTableItem[] = [];
  public columns = [
    'checklist',
    'project',
    'request_type',
    'details',
    'urgency',
    'status',
    'approvals',
  ];
  private approvalsSub: Subscription;
  constructor(
    public approvalsService: ConsentApprovalsService,
    public dialog: MatDialog
  ) {}

  ngOnInit() {
    this.approvalsService.getConsentRequestsWithApprovals();
    this.formSubscription = this.filterControls.valueChanges
      .pipe(
        startWith(this.filterControls.value),
        pairwise(),
        debounce(([oldVal, newVal]) =>
          oldVal.received_date !== newVal.received_date ||
          oldVal.timing_date !== newVal.timing_date
            ? timer(500)
            : timer(0)
        )
      )
      .subscribe(([old, data]) => {
        let input = { ...data } as unknown as ConsentRequestFilterInput;
        let hasChanged = false;
        for (let key of Object.keys(
          input
        ) as unknown[] as (keyof ConsentRequestFilterInput)[]) {
          if (input[key] === null) {
            delete input[key];
          } else if (key === 'project_id') {
            if (typeof input[key] !== 'string') {
              input[key] = (input[key] as unknown as Project).id;
            } else {
              delete input[key];
            }
          } else if (
            typeof input[key] === 'string' &&
            input[key]?.length === 0
          ) {
            input[key] = null;
          } else if (key === 'received_date') {
            let value = input[key] as any;
            input[key] =
              value instanceof Date
                ? formatDate(value, 'yyyy-MM-dd', 'en-US')
                : null;
          }
          if (this.filters[key] !== input[key]) {
            hasChanged = true;
          }
        }
        if (hasChanged) {
          this.filters = input;
          this.approvalsService.getPage(this.currentPage, this.filters);
        }
      });
    this.approvalsSub = combineLatest({
      requests: this.approvalsService.consentRequests$,
      statuses: this.approvalsService.consentStatuses$,
      requestTypes: this.approvalsService.consentRequestTypes$,
      requiredStatuses: this.approvalsService.consentRequiredStatuses$,
      approvalStatuses: this.approvalsService.consentApprovalStatuses$,
    }).subscribe(({ requests }) => {
      this.data = this._parseRequests(requests.data);
      if (requests.paginatorInfo)
        this._updatePagination(requests.paginatorInfo);
    });
    this.loadingSub = this.approvalsService.approvalsLoading$.subscribe(
      (loading: boolean) => {
        if (loading) {
          this.filterControls.disable();
        } else {
          this.filterControls.enable();
        }
      }
    );
  }
  private _updatePagination(info: PaginatorInfo) {
    if (info.total !== undefined) {
      this.total = info.total;
    }
    if (info.perPage !== undefined) {
      this.perPage = info.perPage;
    }
    if (info.currentPage !== undefined) {
      this.currentPage = info.currentPage - 1;
    }
  }
  openChecklistDialog(id: ID) {
    let sub = this.dialog
      .open(IndividualChecklistDialogComponent, {
        data: { consentRequestId: id },
        width: '100vw',
        maxWidth: '100vw',
        height: '100vh',
      })
      .afterClosed()
      .subscribe((data) => {
        this.getPage({
          pageIndex: this.currentPage - 1,
          pageSize: this.perPage,
          length: this.total,
        });
      });
  }
  private _parseRequests(requests: ConsentRequest[]): RequestTableItem[] {
    let tableItems: RequestTableItem[] = [];
    for (let request of requests) {
      let item: RequestTableItem = {
        project: request.project,
        checklist: IndicatorState.INITIAL,
        developer: request.project.developer ?? null,
        sponsor: request.project.participants?.find((participant: Participant) => participant.role.name == 'Sponsor')?.sponsor ?? null,
        requestType:
          this.approvalsService.getRequestType(request.consent_request_type_id)
            ?.request_type ?? '\u2014',
        status:
          this.approvalsService.getStatus(request.consent_status_id)?.status ??
          '\u2014',
        timing: request.timing_date
          ? formatDate(request.timing_date, 'MM/dd/yyyy', 'en-US')
          : '\u2014',
        received: request.received_date
          ? formatDate(request.received_date, 'MM/dd/yyyy', 'en-US')
          : '\u2014',
        is_urgent: request.is_urgent_flag,
        details: request.details,
        urgency_note: request.urgency_note,
        approvals: request.consentRequestApprovals
          ? _parseApprovals(
              request.consentRequestApprovals,
              this.approvalsService
            )
          : [],
        request,
      };
      if (request.hasPastDue) {
        if (item.is_urgent) {
          item.checklist = IndicatorState.ERROR;
        } else {
          item.checklist = IndicatorState.ALERT;
        }
      }
      tableItems.push(item);
    }
    return tableItems;
  }
  public getPage($event: PageEvent) {
    let filters = undefined;
    if (Object.keys(this.filters).length > 0) {
      filters = this.filters;
    }
    this.approvalsService.getPage($event.pageIndex + 1, filters);
  }
  ngOnDestroy() {
    this.approvalsSub?.unsubscribe();
    this.formSubscription?.unsubscribe();
    this.loadingSub.unsubscribe();
  }
}
